import AdditionsGroup from './AdditionsGroup';
import ExceptionsGroup from './ExceptionsGroup';
import LinePriceBreakdown from '../Price/LinePriceBreakdown';
import { useOrderItemOptions } from '../../hooks/useOrderItemOptions';
import { usePriceCheck } from '../../hooks/usePriceCheck';
import type { Product } from '../../types/@types/product';
import type { OrderItemInput, LinePriceCalc } from '../../types/@types/order';
import { useEffect, useState } from 'react';
import { buildKitchenSummary } from '../../utils/kitchen';

export default function OptionsModal({
  open,
  onClose,
  product,
  quantity = 1,
  onApply,
  flavorsNames,
}: {
  open: boolean;
  onClose: () => void;
  product: Product;
  quantity?: number;
  flavorsNames?: string[];
  onApply: (opts: {
    additions: Record<string, number>;
    exceptions: string[];
    linePriceCalc?: LinePriceCalc;
    kitchenSummary?: string;
  }) => void;
}) {
  const {
    additions,
    exceptions,
    setAdditions,
    incAddition,
    decAddition,
    toggleException,
    cfg,
  } = useOrderItemOptions(product);
  const priceCheck = usePriceCheck();
  const [calc, setCalc] = useState<LinePriceCalc | undefined>();

  useEffect(() => {
    if (!open || !(product as any)?.id) return;
    const t = setTimeout(async () => {
      const items: OrderItemInput[] = [
        { product: (product as any).id, quantity, additions, exceptions },
      ];
      const res = await priceCheck.mutateAsync(items);
      const first = res?.items?.[0];
      setCalc(first?.linePriceCalc);
    }, 250);
    return () => clearTimeout(t);
  }, [open, product, quantity, additions, exceptions, priceCheck]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Personaliza tu {product?.name}</h3>
          <button className="text-sm underline" onClick={onClose}>
            Cerrar
          </button>
        </div>

        {cfg.additions.length > 0 && (
          <section className="mb-4">
            <h4 className="font-medium mb-2">Adiciones</h4>
            <AdditionsGroup
              options={cfg.additions}
              value={additions}
              onChange={(key, qty) =>
                setAdditions((prev) => ({ ...prev, [key]: qty }))
              }
            />
          </section>
        )}

        {cfg.exceptions.length > 0 && (
          <section className="mb-4">
            <h4 className="font-medium mb-2">Sin / Excepciones</h4>
            <ExceptionsGroup
              options={cfg.exceptions}
              selected={exceptions}
              onToggle={toggleException}
            />
          </section>
        )}

        <LinePriceBreakdown calc={calc} />

        <div className="mt-4 flex justify-end gap-2">
          <button className="px-4 py-2 rounded border" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="px-4 py-2 rounded bg-gray-900 text-white"
          onClick={() => {
              const kitchenSummary = buildKitchenSummary({
                flavorsNames,
                additions,
                additionsCatalog: cfg.additions,
                exceptions,
                exceptionsCatalog: cfg.exceptions,
              });
              onApply({ additions, exceptions, linePriceCalc: calc, kitchenSummary });
              onClose();
            }}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
